/** The value actions of the AI-changes pages — one family, one links module (the detail-page
 *  value actions rule). Every href comes from `gridLinks`; `aiChangeLinks.test.ts` proves each
 *  one lands on a real route. Filter links write the grid's own URL params (`tenant`, `actor`,
 *  `channel`, `status`, `client`, `from` / `to`, `batch_id`), so the toolbar shows the filter they
 *  set. */
import type { AIChangeActor, AIChangeChannel, AIChangeStatus } from '@/api/aiChanges';
import { channelLabel, statusLabel } from '@/api/aiChanges';
import type { GridLink } from '@/components/ValueActions/identity';
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import { aiChangesHref, auditHref, plainHref } from '@/utils/gridLinks';

/** The detail page of one change set. */
export const aiChangeHref = (id: string) => `/ai-changes/${encodeURIComponent(id)}`;

/** The AI spend booked under a change set (its id is the correlation id of every call). */
export const aiChangeSpendHref = (id: string) => plainHref('/ai-spend', { correlation_id: id });

/** The operator part of a header: "AI changes of this operator". */
export const tenantGrid = (tenantId: number): GridLink => ({
  href: aiChangesHref({ tenant: tenantId }),
  label: 'AI changes of this operator',
});

/** A change set named on another change set's page (its original, a revert, a conflict). */
export function changeSetActions(id: string): ValueAction[] {
  return [
    act.open(aiChangeHref(id), 'Open this AI change'),
    act.filter(aiChangesHref({ revert_of: id }), 'Reverts of this change set'),
    act.copy(id, 'Copy change-set id'),
  ];
}

/** The batch of a content apply (one change set per record): its change sets in the grid. */
export const batchHref = (batchId: string) => aiChangesHref({ batch_id: batchId });

export function batchActions(batchId: string): ValueAction[] {
  return [
    act.filter(batchHref(batchId), 'Change sets of this batch'),
    act.copy(batchId, 'Copy batch id'),
  ];
}

export function actorActions(actor: AIChangeActor): ValueAction[] {
  return [
    ...(actor.id !== null ? [act.open(`/users/${actor.id}`, 'Open user')] : []),
    ...(actor.id !== null
      ? [act.filter(aiChangesHref({ actor: actor.id }), 'AI changes by this user')]
      : []),
    act.filter(auditHref({ actor_email: actor.email }), 'Audit entries by this user'),
    act.copy(actor.email, 'Copy email'),
  ];
}

export function channelActions(channel: AIChangeChannel): ValueAction[] {
  return [act.filter(aiChangesHref({ channel }), `Only ${channelLabel(channel)} change sets`)];
}

export function statusActions(status: AIChangeStatus): ValueAction[] {
  return [
    act.filter(aiChangesHref({ status }), `Only ${statusLabel(status).toLowerCase()} change sets`),
  ];
}

export function clientActions(client: { id: string; name: string }): ValueAction[] {
  return [
    act.filter(aiChangesHref({ client: client.id }), 'AI changes by this MCP client'),
    act.copy(client.id, 'Copy client id'),
  ];
}

export function ipActions(ip: string): ValueAction[] {
  return [act.copy(ip, 'Copy IP'), act.whois(ip)];
}

const HOUR_MS = 3_600_000;

/** Other change sets around a moment (±1 h) — what else the same session or sync did. */
export function momentActions(iso: string): ValueAction[] {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return [];
  return [
    act.filter(
      aiChangesHref({
        from: new Date(t - HOUR_MS).toISOString(),
        to: new Date(t + HOUR_MS).toISOString(),
      }),
      'AI changes within ±1 h',
    ),
    act.copy(iso, 'Copy timestamp'),
  ];
}
